import { useCallback, useEffect, useState } from "react";
import { HttpError, request } from "../../../api/http";
import { useConfirm } from "../../../components/Dialog";
import { useAuth } from "../../../context/AuthContext";

type Observation = {
  id: string;
  leadUlid: string;
  body: string;
  authorUsername: string;
  createdAt: string;
  updatedAt: string;
};

type ListResponse = { observations: Observation[] };

const MAX_BODY = 4000;

const fmtDateTime = (iso: string) => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

export default function Observations({ leadUlid }: { leadUlid: string }) {
  const { token } = useAuth();
  const confirm = useConfirm();
  const [items, setItems] = useState<Observation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newBody, setNewBody] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await request<ListResponse>(
        `/api/admin/operations/onboarding/${leadUlid}/observations`,
        { token },
      );
      setItems(data.observations);
    } catch (err) {
      setError(
        err instanceof HttpError
          ? `HTTP ${err.status}`
          : "No se pudo cargar",
      );
    } finally {
      setLoading(false);
    }
  }, [leadUlid, token]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    const body = newBody.trim();
    if (!body || submitting) return;
    setSubmitting(true);
    try {
      const created = await request<Observation>(
        `/api/admin/operations/onboarding/${leadUlid}/observations`,
        { method: "POST", body: { body }, token },
      );
      setItems((prev) => [created, ...prev]);
      setNewBody("");
    } catch (err) {
      setError(
        err instanceof HttpError
          ? `No se pudo guardar (HTTP ${err.status})`
          : "No se pudo guardar",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (id: string, body: string) => {
    try {
      const updated = await request<Observation>(
        `/api/admin/operations/onboarding/observations/${id}`,
        { method: "PATCH", body: { body }, token },
      );
      setItems((prev) => prev.map((i) => (i.id === id ? updated : i)));
    } catch (err) {
      setError(
        err instanceof HttpError
          ? `No se pudo guardar (HTTP ${err.status})`
          : "No se pudo guardar",
      );
    }
  };

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: "Eliminar observación",
      message: "¿Eliminar esta observación? Esta acción no se puede deshacer.",
      confirmLabel: "Eliminar",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await request<unknown>(
        `/api/admin/operations/onboarding/observations/${id}`,
        { method: "DELETE", token },
      );
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (err) {
      setError(
        err instanceof HttpError
          ? `No se pudo borrar (HTTP ${err.status})`
          : "No se pudo borrar",
      );
    }
  };

  return (
    <div
      style={{
        marginTop: "0.85rem",
        background: "white",
        padding: "0.85rem 1rem",
        borderRadius: "0.55rem",
        borderLeft: "3px solid #856404",
        boxShadow: "0 1px 3px rgba(0,0,0,.04)",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <h4
        style={{
          margin: "0 0 0.6rem",
          fontSize: "0.72rem",
          textTransform: "uppercase",
          fontWeight: 600,
          color: "#856404",
          letterSpacing: "0.5px",
        }}
      >
        Observaciones
      </h4>

      {error && (
        <div
          style={{
            color: "#b32424",
            fontSize: ".8rem",
            marginBottom: ".5rem",
          }}
        >
          {error}
        </div>
      )}

      <form
        onSubmit={handleCreate}
        style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem" }}
      >
        <input
          type="text"
          value={newBody}
          onChange={(e) => setNewBody(e.target.value)}
          placeholder="Añadir observación…"
          maxLength={MAX_BODY}
          style={{ flex: 1 }}
        />
        <button
          type="submit"
          disabled={submitting || newBody.trim().length === 0}
        >
          {submitting ? "Guardando…" : "Añadir"}
        </button>
      </form>

      {loading && (
        <div style={{ fontSize: ".8rem", color: "#73879C" }}>Cargando…</div>
      )}
      {!loading && items.length === 0 && (
        <div style={{ fontSize: ".8rem", color: "#73879C" }}>
          Sin observaciones todavía.
        </div>
      )}

      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {items.map((item) => (
          <ObservationItem
            key={item.id}
            item={item}
            onSave={(body) => handleUpdate(item.id, body)}
            onDelete={() => handleDelete(item.id)}
          />
        ))}
      </ul>
    </div>
  );
}

function ObservationItem({
  item,
  onSave,
  onDelete,
}: {
  item: Observation;
  onSave: (body: string) => Promise<void>;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(item.body);
  const [saving, setSaving] = useState(false);

  const cancel = () => {
    setBody(item.body);
    setEditing(false);
  };

  const save = async () => {
    const trimmed = body.trim();
    if (!trimmed || trimmed === item.body) {
      cancel();
      return;
    }
    setSaving(true);
    try {
      await onSave(trimmed);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <li
      style={{
        padding: "0.55rem 0",
        borderTop: "1px solid #f1f3f5",
        fontSize: ".82rem",
        display: "flex",
        gap: "0.75rem",
        alignItems: "flex-start",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        {editing ? (
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={MAX_BODY}
            rows={2}
            style={{
              width: "100%",
              fontFamily: "inherit",
              fontSize: "inherit",
              padding: "0.4rem 0.5rem",
              border: "1px solid #dee2e6",
              borderRadius: "0.4rem",
              background: "#f8f9fb",
            }}
          />
        ) : (
          <div
            style={{
              color: "#1f2933",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {item.body}
          </div>
        )}
        <div
          style={{
            marginTop: "0.25rem",
            fontSize: ".7rem",
            color: "#73879C",
          }}
        >
          <strong>{item.authorUsername}</strong> · {fmtDateTime(item.createdAt)}
          {item.updatedAt !== item.createdAt && (
            <span> · editada {fmtDateTime(item.updatedAt)}</span>
          )}
        </div>
      </div>
      <div style={{ display: "flex", gap: "0.25rem" }}>
        {editing ? (
          <>
            <button type="button" onClick={save} disabled={saving}>
              {saving ? "…" : "Guardar"}
            </button>
            <button
              type="button"
              onClick={cancel}
              disabled={saving}
              style={{ background: "#73879C" }}
            >
              Cancelar
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setEditing(true)}
              style={{ background: "#f8f9fb", color: "#1f2933" }}
              title="Editar"
            >
              ✎
            </button>
            <button
              type="button"
              onClick={onDelete}
              style={{ background: "#fde2e2", color: "#b32424" }}
              title="Eliminar"
            >
              ✕
            </button>
          </>
        )}
      </div>
    </li>
  );
}
